/**
 * @file components/Heading/component.js
 * @copyright 2021 CBC/Radio-Canada. All rights reserved.
 *
 */
import { useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import classNames from "classnames";
import IconChevron from "../Icon/IconChevron";
import LinkWrap from "./../LinkWrap/component";
import styles from "./styles/themes/heading.module.scss";
import OZEMPICS_SPONSOR from "./resources/images/ozempic-sponsor.png";

/**
 * Heading
 * @component
 * Renders text in a heading tag, with additional JSX content added to main (left positioned), and right (right positioned) containers.
 * Optionally renders heading tag inside of LinkWrap with a chevron icon
 */
export const Heading = ({
    level,
    text,
    link = "",
    external = false,
    main = null,
    right = null,
    className,
    levelClassName,
}) => {
    //Turn the tag string into JSX with capital letter
    const HeadingTag = level;
    const headingElementClasses = classNames(
        "heading-element",
        "heading-element-" + (levelClassName || level)
    );
    return (
        <div
            className={classNames("heading", className, styles.heading)}
            data-testid="heading"
        >
            <div className="heading-main">
                {
                    //Conditionally render the link with chevron
                    link ? (
                        <LinkWrap
                            url={link}
                            external={external}
                            className="heading-link sclt-headingLink"
                            data-testid="heading-link"
                            data-cy="heading-link"
                        >
                            <HeadingTag className={headingElementClasses}>
                                {text}
                                <IconChevron
                                    width={"15px"}
                                    height={"15px"}
                                    className={classNames(
                                        "horizontal",
                                        "headingIcon"
                                    )}
                                />
                            </HeadingTag>
                        </LinkWrap>
                    ) : text && text.trim() ? (
                        <HeadingTag
                            className={headingElementClasses}
                            data-cy="heading-element"
                        >
                            {text}
                        </HeadingTag>
                    ) : null
                }
                {main}
            </div>
            {
                //Conditionally render a right container
                right && <div className="heading-right">{right}</div>
            }
        </div>
    );
};

const LevelProps = PropTypes.oneOf(["h1", "h2", "h3", "h4"]);

Heading.propTypes = {
    level: LevelProps.isRequired, //Determines the heading tag
    // levelClassName optional - enables overriding of the default heading style
    // e.g. styling an h2 to look like an h3
    // <Heading level="h2" levelClassName="h3" />
    levelClassName: LevelProps,
    text: PropTypes.string, //Text for heading element
    link: PropTypes.string, // Optional link  for heading element
    external: PropTypes.bool, //True when the link is external
    main: PropTypes.element, //Additional JSX content to be sibling to heading element within main container
    right: PropTypes.element, //Additional JSX content to be in right container
    className: PropTypes.string, //Additional class names added to the heading container
};

/**
 * PageHeading
 * @component
 * Used once per page to render an heading with H1 tag
 * Has an optional description
 * If hidden is true, will be visually hidden but available to screen readers.
 */
export const PageHeading = ({ text, description = "", hidden = false }) => {
    const location = useLocation();
    //TODO: This is a temporary solution for the sponsorship banner. This should be removed once Olympics is over.
    const showSponsorship =
        location?.pathname === "/sports/olympics/summer/basketball" ||
        location?.pathname === "/sports/olympics/summer/basketball/schedule";

    const classes = classNames(
        "heading-pageHeading",
        hidden === true ? "a11y" : "",
        showSponsorship ? "heading--olympicSponsorLayout" : ""
    );

    return (
        <Heading
            level={"h1"}
            text={text}
            main={
                description ? (
                    <p
                        className="heading-description"
                        suppressHydrationWarning={true}
                    >
                        {description}
                    </p>
                ) : null
            }
            right={
                showSponsorship ? (
                    <SponsoredSectionHeading
                        sponsoredImageUrl={OZEMPICS_SPONSOR}
                        sponsoredImageAlt="Ozempic"
                        sponsoredImageLink="https://www.ozempic.ca/"
                        external={true}
                        variant={"Presented by"}
                        text={""}
                    />
                ) : null
            }
            className={classes}
        />
    );
};

PageHeading.propTypes = {
    text: PropTypes.string.isRequired, //Text for heading element
    description: PropTypes.string, //Text for optional page description
    hidden: PropTypes.bool, // Visually hides H1 tag with a11y class, will be seen by assistive technologies
};

/**
 * SectionHeading
 * @component
 * Renders text in an H2 tag
 * Use only one SectionHeading  per <section> tag. For additional headings in a section use SubHeading.
 * Has an optional description
 * Optional Link
 */
export const SectionHeading = ({
    text,
    description = "",
    link = "",
    external = false,
}) => {
    return (
        <Heading
            level="h2"
            text={text}
            link={link}
            external={external}
            main={
                description ? (
                    <p
                        className="heading-description"
                        data-testid="heading-description"
                    >
                        {description}
                    </p>
                ) : null
            }
            className={`heading-sectionHeading ${styles.sectionHeading}`}
        />
    );
};

SectionHeading.propTypes = {
    text: PropTypes.string, //Text for heading element
    description: PropTypes.string, //Text for optional page description
    link: PropTypes.string, // Optional link  for heading element
    external: PropTypes.bool, //True when the link is external
};

/**
 * SponsoredSectionHeading
 * @component
 * Renders text in an H2 tag
 * Use in place of SectionHeading component when a sponsored image is needed.
 * Optional Link which is applied both to the H2 element and to the sponsored image element.
 * Cannot have a description due to lack of screen real estate
 */
export const SponsoredSectionHeading = ({
    link = "",
    external = false,
    text,
    sponsoredImageUrl,
    sponsoredImageAlt = "",
    sponsoredImageLink,
    variant,
    size,
    headingLevel,
    darkMode,
}) => {
    const sponsoredImg = (
        <div className="heading-sectionHeading-sponsoredImageWrap">
            <img
                alt={sponsoredImageAlt}
                src={sponsoredImageUrl}
                loading="lazy"
                className={`heading-sectionHeading-sponsoredImageWrap-image${
                    size ? "-" + size : ""
                }`}
            />
        </div>
    );

    //Conditionally render link wrap around sponsored image
    const main = sponsoredImageLink ? (
        <LinkWrap
            url={sponsoredImageLink}
            external={external}
            className={"heading-sectionHeading-sponsoredImageWrap-link"}
        >
            {sponsoredImg}
        </LinkWrap>
    ) : (
        sponsoredImg
    );

    let spanClass = darkMode
        ? `heading-sectionHeading-sponsorText-${darkMode}`
        : "heading-sectionHeading-sponsorText";
    const right =
        variant && !sponsoredImageLink ? (
            <span className={spanClass}>
                <span>Presented by </span>
                {sponsoredImg}
            </span>
        ) : variant && sponsoredImageLink ? (
            <span className={spanClass}>
                <span>Presented by </span>
                {main}
            </span>
        ) : (
            main
        );

    return (
        <Heading
            level={"h2"}
            text={text}
            link={link}
            external={external}
            main={variant ? null : main}
            right={variant ? right : null}
            //Gets the same styles as section heading
            className={classNames("heading-sectionHeading")}
            levelClassName={headingLevel}
        />
    );
};

SponsoredSectionHeading.propTypes = {
    text: PropTypes.string.isRequired, //Text for heading element
    sponsoredImageUrl: PropTypes.string.isRequired, //A URL for the sponsored image file
    sponsoredImageAlt: PropTypes.string, //An alt text for the sponsored image file
    sponsoredImageLink: PropTypes.string, //An optional link for the sponsor image file
    link: PropTypes.string, //Optional link  for heading element
    external: PropTypes.bool, //True when the link is external
    variant: PropTypes.string, //type of text to display next to section heading (eg "Presented by")
    size: PropTypes.string, //section size for sponsor image (sm for small, lg for large)
    headingLevel: PropTypes.string, //Declares what size the heading should rendered at
    darkMode: PropTypes.string, // Declares whether the heading is appearing on a dark background or not
};

/**
 * SubHeading
 * @component
 * Renders text in an H3 tag
 * Used for sub section headings within page sections, also used for headings in the right rail.
 * Optional Link
 */
export const SubHeading = ({ text, link = "", external = false }) => {
    return (
        <Heading
            level={"h3"}
            text={text}
            link={link}
            external={external}
            className={"heading-subHeading"}
        />
    );
};

SubHeading.propTypes = {
    text: PropTypes.string.isRequired, //Text for heading element
    link: PropTypes.string, // Optional link  for heading element
    external: PropTypes.bool, //True when the link is external
};
